import { useState, useEffect } from 'react';
import useSWR from 'swr';
// Resources
import {
  getBlockchainConfigurations,
  getNftCollections,
} from '@/resources/nft-service.resource';

function buildBlockchainCollections(collections, blockchain) {
  const data = [];
  for (let i = 0; i < collections.length; i++) {
    const collection = collections[i];
    const chain = blockchain.find((config) => config.chainId === collection.chainId);
    data.push({
      ...collection,
      contractAddress: collection.contractAddress.toLowerCase(),
      networkName: chain.name,
      rpcUrl: chain.rpcUrl,
      blockExplorerUrl: chain.blockExplorerUrl,
      nativeTokenDecimalPrecision: chain.nativeTokenDecimalPrecision,
      usesEIP1559: chain.usesEIP1559,
      isDefaultLayer1: chain.isDefaultLayer1,
      isDefaultLayer2: chain.isDefaultLayer2,
    });
  }
  return data;
}

async function getCollections() {
  const collectionsResponse = await fetch(getNftCollections().url);
  const collections = await collectionsResponse.json();
  const blockchainsResponse = await fetch(getBlockchainConfigurations().url);
  const blockchains = await blockchainsResponse.json();

  return buildBlockchainCollections(collections, blockchains.blockchainConfigurations);
}

const fetcherSWR = async (url, collections, walletInfo) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ collections, walletInfo }),
  });
  return response.json();
};

export function useNftCollections({ walletInfo }) {
  const [collections, setCollections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({
    header: null,
    body: null,
  });
  const [collectionsList, setCollectionsList] = useState([]);

  const reset = () => {
    setCollections({});
    setCollectionsList([]);
    setLoading(true);
    setError({
      header: null,
      body: null,
    });
    localStorage.clear();
  };

  useEffect(() => {
    // Keep loading true while wallet is still loading
    if (walletInfo.loading) {
      return;
    }

    const fetchCollections = async () => {
      try {
        const collections = await getCollections();
        setCollectionsList(collections);
      } catch (err) {
        setError({
          header: 'Error fetching collections',
          body: 'Please try again later',
        });
      }
    };

    fetchCollections();
  }, [walletInfo]);

  const {
    data: collectionData,
    isLoading: collectionDataLoading,
    error: collectionDataError,
    mutate: refreshCollectionsData,
  } = useSWR(
    collectionsList.length > 0 && walletInfo && (walletInfo.core || walletInfo.legacy)
      ? ['/api/nfts/collectionData', collectionsList, walletInfo]
      : null,
    ([url, collections, walletInfo]) => fetcherSWR(url, collections, walletInfo),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (collectionDataError) {
      setError({
        header: 'Error fetching collections NFTs',
        body: 'Please try again later',
      });
    }
  }, [collectionDataError]);

  useEffect(() => {
    if (collectionData && collectionsList.length > 0) {
      const collectionsMap = {};
      collectionsList.forEach((collection, index) => {
        // Check if there are NFTs in either core or legacy wallet
        const hasCoreNfts =
          collectionData[index]?.nfts?.core &&
          Object.keys(collectionData[index].nfts.core).length > 0;
        const hasLegacyNfts =
          collectionData[index]?.nfts?.legacy &&
          Object.keys(collectionData[index].nfts.legacy).length > 0;

        if (hasCoreNfts || hasLegacyNfts) {
          collectionsMap[collection.contractAddress] = {
            ...collection,
            ...collectionData[index],
          };
        }
      });
      setCollections(collectionsMap);
      setLoading(false);
    }
  }, [collectionData, collectionsList]);

  const fetchAndUpdateSingleCollection = async (collection) => {
    try {
      const response = await fetch('/api/nfts/collectionData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collections: [collection], walletInfo }),
      });
      const [data] = await response.json();

      // Only update if there are NFTs in core or legacy
      const hasCoreNfts = data?.nfts?.core && Object.keys(data.nfts.core).length > 0;
      const hasLegacyNfts =
        data?.nfts?.legacy && Object.keys(data.nfts.legacy).length > 0;

      if (hasCoreNfts || hasLegacyNfts) {
        setCollections((prev) => ({
          ...prev,
          [collection.contractAddress]: {
            ...collection,
            ...data,
          },
        }));
        return {
          ...collection,
          ...data,
        };
      } else {
        // Remove the collection if it has no NFTs
        setCollections((prev) => {
          const updated = { ...prev };
          delete updated[collection.contractAddress];
          return updated;
        });
        return null;
      }
    } catch (err) {
      setError({
        header: 'Error fetching collection NFTs',
        body: 'Please try again later',
      });
    }
  };

  return {
    collections,
    loading,
    collectionDataLoading,
    error,
    reset,
    getCollectionByAddress: (contractAddress) => collections[contractAddress],
    refreshCollectionsData,
    fetchAndUpdateSingleCollection,
  };
}
